/**
 * Minimal localStorage wrapper for the operator's JWT.
 *
 * We store three fields:
 *   - token:     the signed JWT
 *   - expiresAt: ISO timestamp from the login/signup response
 *   - userJson:  the CurrentUser view returned alongside the token so the UI
 *                can hydrate the logged-in identity without an extra GET /me
 *
 * All three are cleared together on logout or on 401s from the server.
 */
import type { CurrentUser } from "@shared/api/schemas/auth";

const KEY_TOKEN = "signallens.jwt";
const KEY_EXPIRES = "signallens.jwt.expiresAt";
const KEY_USER = "signallens.user";

export interface StoredSession {
	token: string;
	expiresAt: string;
	user: CurrentUser;
}

export const jwtStore = {
	get(): StoredSession | null {
		const token = localStorage.getItem(KEY_TOKEN);
		const expiresAt = localStorage.getItem(KEY_EXPIRES);
		const userJson = localStorage.getItem(KEY_USER);
		if (!token || !expiresAt || !userJson) return null;
		if (new Date(expiresAt).getTime() <= Date.now()) {
			jwtStore.clear();
			return null;
		}
		try {
			return { token, expiresAt, user: JSON.parse(userJson) as CurrentUser };
		} catch {
			jwtStore.clear();
			return null;
		}
	},
	set(session: StoredSession): void {
		localStorage.setItem(KEY_TOKEN, session.token);
		localStorage.setItem(KEY_EXPIRES, session.expiresAt);
		localStorage.setItem(KEY_USER, JSON.stringify(session.user));
	},
	clear(): void {
		localStorage.removeItem(KEY_TOKEN);
		localStorage.removeItem(KEY_EXPIRES);
		localStorage.removeItem(KEY_USER);
	},
};
